import MaldivesBonusInclusionsCampaignLabel, { hasMaldivesBonusInclusionsCampaignTag } from 'components/Common/Labels/MaldivesBonusInclusionsCampaignLabel'
import SearchMapInclusions from 'components/Search/SearchResultsMap/SearchMapInclusions'
import OfferInclusionsList from 'components/Common/Inclusions/OfferInclusionsList'
import DatesAvailable2027CampaignLabel, { hasDatesAvailable2027CampaignTag } from 'components/Common/Labels/DatesAvailable2027CampaignLabel'
import Modal from 'LuxKit/Modal/Modal'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import useOfferUrl from 'hooks/Offers/useOfferUrl'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import { EmptyArray } from 'lib/array/arrayUtils'
import { reorderTileInclusionsByActiveFilters } from 'lib/offer/inclusionUtils'
import { isFlashHotelOffer } from 'lib/offer/offerTypes'
import LuxPlusLabelWithModal from 'luxPlus/components/LuxPlusLabelWithModal'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import ExperimentalSearchTileFeaturesList, { MAX_INCLUSIONS_TO_SHOW } from '../shared/ExperimentalSearchTileFeaturesList'
import SearchTileFreeCancellationInclusion, { type SearchTileCancellationInclusionKind, useSearchTileCancellationInclusion } from '../shared/SearchTileFreeCancellationInclusion'
import { getCompactInclusionListLimit } from './accommodationSearchTileView'
import type { SearchTileRowInclusion } from '../shared/SearchTileRowInclusionItem'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import useProperty from 'hooks/Offers/useProperty'
import useBedbankRates from 'hooks/useBedbankRates'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ExperimentalHotelUpgradableInclusionsLabel from './ExperimentalHotelUpgradableInclusionsLabel'
import SearchTileRoomBreakdown from '../shared/SearchTileRoomBreakdown'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import LuxPlusInclusions from '../../../../../luxPlus/components/LuxPlusInclusions'
import LoyaltyInclusions from 'loyalty/components/LoyaltyInclusions'
import useLoyaltyBenefitInclusions from 'loyalty/hooks/useLoyaltyBenefitInclusions'
import { mapPropertyPopularFacilitiesToAmenities } from './propertyPopularFacilities'
import { useActiveInclusionFilters } from 'hooks/useActiveInclusionFilters'
import ExperiencesPassportOfferSummary from 'components/Experiences/ExperiencesPassport/ExperiencesPassportOfferSummary'
import { CHARTER_FLIGHT_DETAILS, MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'
import { isConfiguredFeederOriginAirportCode, rewriteCharterReturnFlightsGuestInclusions, rewriteOneWayCharterInclusions } from 'lib/charterFlights/charterBundleOfferPageUtils'
import { useMaldivesDirectBundleContext } from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectBundleContext'
import useExperiencesPassportForOffer from 'components/Experiences/ExperiencesPassport/useExperiencesPassportForOffer'
import { resolveMappedSearchFilterInclusions, shouldShowCompactMappedInclusionsOverflow, useSearchFilterCategoryInclusions } from '../shared/useSearchFilterCategoryInclusions'
import { getUpgradableInclusions } from './upgradableInclusionsUtils'

const DEFAULT_CHARTER_SEARCH_ROOMS: Array<App.Occupants> = [{ adults: 2 }]
const MAX_VISIBLE_INCLUSIONS_TO_SHOW_INLINE_PASSPORT_EXPERIENCES = 3

interface Props {
  mapLayout?: 'row' | 'card'
  bestPackage?: App.HotelPackage
  filters?: App.OfferListFilters
  offer: App.HotelOffer | App.HotelOfferSummary
  offerMetaData?: App.OfferListMetaData
  maxInclusionsToShow?: number
  hideSecondaryLuxPlusPromos?: boolean
  hideLuxPlusLabels?: boolean
  hideMemberSavingsLabel?: boolean
  hideUpgradableInclusionsLabel?: boolean
  hideRoomBreakdown?: boolean
  maxAmenitiesToShow?: number
  hideLoyaltyInclusions?: boolean
  mapToSearchFilterCategories?: boolean
  cancellationInclusionKind?: SearchTileCancellationInclusionKind
}

function ExperimentalHotelSearchTileFeatures({
  bestPackage,
  mapLayout,
  filters,
  offer,
  offerMetaData,
  maxInclusionsToShow,
  hideSecondaryLuxPlusPromos = false,
  hideLuxPlusLabels = false,
  hideMemberSavingsLabel = false,
  hideUpgradableInclusionsLabel = false,
  hideRoomBreakdown = false,
  maxAmenitiesToShow,
  hideLoyaltyInclusions = false,
  mapToSearchFilterCategories = false,
  cancellationInclusionKind,
}: Props) {
  const intl = useIntl()
  const metaData = useOfferMetaData(offer.id, filters)
  const [propertyContent, isFetchingPropertyContent] = useProperty(metaData?.accommodationId)

  const { inclusions, bonusInclusions, luxPlusInclusions } = useOfferInclusions(
    bestPackage,
    {
      checkIn: filters?.checkIn,
      checkOut: filters?.checkOut,
    },
  )

  const isBedbankRate = metaData?.pricing.rateType === 'bedbank'

  const [{ hotelOnlyRates: bedbankRate }, isFetchingBedbankRates] = useBedbankRates(
    isBedbankRate ? metaData?.pricing.ratePropertyId : undefined,
    filters?.rooms,
    filters?.checkIn,
    filters?.checkOut,
    isBedbankRate,
  )

  const bedbankRateForRoom = useMemo(() => {
    if (!isBedbankRate) return undefined
    const roomRateId = metaData?.pricing.lowestPriceRoomRateId
    if (!roomRateId || !bedbankRate.length) return undefined

    return bedbankRate.flatMap(rates => rates).find(rate => rate.id === roomRateId)
  }, [isBedbankRate, metaData?.pricing.lowestPriceRoomRateId, bedbankRate])

  const isLuxPlusMember = useAppSelector(getCanViewLuxPlusBenefits)
  const activeInclusionCategories = useActiveInclusionFilters()
  const isFlashHotel = isFlashHotelOffer(offer)
  const isCharterBundleOffer = offer.campaignTags.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG)
  const { oneWayBundle, departureAirportCode, departureAirportName } = useMaldivesDirectBundleContext()
  const charterJourney = metaData?.suggestedTravelDates?.charterJourney
  const isOneWayBundle = charterJourney ? charterJourney.completeness !== 'complete' : oneWayBundle

  const getEffectiveInclusions = useCallback(<T extends { description: string; symbol?: string }>(inclusions: Array<T>): Array<T> => {
    if (!isCharterBundleOffer) {
      return inclusions
    }

    if (isOneWayBundle) {
      const { originAirport, destinationAirport } = CHARTER_FLIGHT_DETAILS['melbourne-maldives']
      return rewriteOneWayCharterInclusions(inclusions, {
        feederCity: isConfiguredFeederOriginAirportCode('melbourne-maldives', departureAirportCode) ? departureAirportName : undefined,
        originCity: originAirport.cityDesc,
        destinationCity: destinationAirport.cityDesc,
        intl,
      })
    }

    return rewriteCharterReturnFlightsGuestInclusions(inclusions, {
      rooms: filters?.rooms?.length ? filters.rooms : DEFAULT_CHARTER_SEARCH_ROOMS,
      isDirectFlight: !isConfiguredFeederOriginAirportCode('melbourne-maldives', departureAirportCode),
      intl,
    })
  }, [departureAirportCode, departureAirportName, filters?.rooms, intl, isCharterBundleOffer, isOneWayBundle])

  const luxPlusInclusionsThatCanBeDisplayed = useMemo<Array<App.OfferInclusion>>(() => {
    return isLuxPlusMember ? luxPlusInclusions : EmptyArray
  }, [isLuxPlusMember, luxPlusInclusions])

  const tileInclusions = useMemo<Array<SearchTileRowInclusion>>(() => {
    if (!isFlashHotel) return EmptyArray
    if (!offer.tileInclusions?.length) return EmptyArray
    return reorderTileInclusionsByActiveFilters(getEffectiveInclusions(offer.tileInclusions), activeInclusionCategories)
  }, [activeInclusionCategories, getEffectiveInclusions, isFlashHotel, offer.tileInclusions])

  const hasOfferInclusions = bonusInclusions.length > 0 || inclusions.length > 0
  const bedbankInclusions = useMemo<Array<App.OfferInclusion>>(() => {
    if (!bedbankRateForRoom?.inclusions?.length) return EmptyArray
    return bedbankRateForRoom.inclusions.filter(inclusion => inclusion.description !== 'Room only')
  }, [bedbankRateForRoom?.inclusions])
  const isFetchingPropertyAmenities = !!metaData?.accommodationId &&
    isFetchingPropertyContent &&
    !propertyContent &&
    !bedbankInclusions.length &&
    !tileInclusions.length &&
    !hasOfferInclusions

  const propertyContentAmenities = useMemo<Array<SearchTileRowInclusion>>(() => {
    if (!propertyContent?.popularFacilities.length) return EmptyArray
    return mapPropertyPopularFacilitiesToAmenities(propertyContent.popularFacilities)
  }, [propertyContent?.popularFacilities])

  const featuresToDisplay = useMemo<{
    featureList: Array<SearchTileRowInclusion>
    featureType: 'inclusions' | 'amenities'
  }>(() => {
    if (bedbankInclusions.length > 0) {
      return { featureList: bedbankInclusions, featureType: 'inclusions' }
    }

    // Content curates per-property USPs for charter bundles, which package inclusions would flatten
    if ((mapLayout === 'row' || isCharterBundleOffer) && tileInclusions.length > 0) {
      return { featureList: tileInclusions, featureType: 'inclusions' }
    }

    if (hasOfferInclusions) {
      return {
        featureList: getEffectiveInclusions([...inclusions, ...bonusInclusions]),
        featureType: 'inclusions',
      }
    }

    if (tileInclusions.length > 0) {
      return { featureList: tileInclusions, featureType: 'inclusions' }
    }

    const fallbackAmenities = offer.lowestPricePackage?.roomType.amenities ?? EmptyArray
    const amenities = propertyContentAmenities.length > 0 ? propertyContentAmenities : fallbackAmenities
    if (amenities.length > 0) return { featureList: amenities, featureType: 'amenities' }

    return { featureList: EmptyArray, featureType: 'inclusions' }
  }, [bedbankInclusions, bonusInclusions, getEffectiveInclusions, hasOfferInclusions, inclusions, isCharterBundleOffer, offer.lowestPricePackage?.roomType.amenities, propertyContentAmenities, tileInclusions, mapLayout])

  const loyaltyBenefitCount = useLoyaltyBenefitInclusions(offer).length
  const loyaltyCount = hideLoyaltyInclusions ? 0 : loyaltyBenefitCount
  const maxFeaturesToShow = featuresToDisplay.featureType === 'amenities' ? maxAmenitiesToShow : maxInclusionsToShow
  const showLoyaltyInline = !hideLoyaltyInclusions && (
    featuresToDisplay.featureType === 'amenities' ||
    featuresToDisplay.featureList.length + loyaltyBenefitCount <= MAX_INCLUSIONS_TO_SHOW
  )

  const { passport } = useExperiencesPassportForOffer({ offer, filters })
  const showPassportInline = !!passport && featuresToDisplay.featureList.length <= MAX_VISIBLE_INCLUSIONS_TO_SHOW_INLINE_PASSPORT_EXPERIENCES
  const passportCount = passport?.inclusions.length ?? 0

  const hiddenFeaturesCount = (showLoyaltyInline ? 0 : loyaltyCount) + (showPassportInline ? 0 : passportCount)
  const cancellationInclusion = useSearchTileCancellationInclusion(cancellationInclusionKind)
  const mappedSearchFilterInclusions = useSearchFilterCategoryInclusions(
    featuresToDisplay.featureType === 'inclusions' ? featuresToDisplay.featureList : EmptyArray,
    maxFeaturesToShow ?? MAX_INCLUSIONS_TO_SHOW,
  )
  const displayFeatures = useMemo(() => {
    const mapped = resolveMappedSearchFilterInclusions(
      mapToSearchFilterCategories && featuresToDisplay.featureType === 'inclusions',
      mappedSearchFilterInclusions.inclusions,
      featuresToDisplay.featureList,
    )
    if (!mapToSearchFilterCategories || featuresToDisplay.featureType !== 'inclusions' || !cancellationInclusion) {
      return mapped
    }
    return [cancellationInclusion, ...mapped]
  }, [cancellationInclusion, featuresToDisplay.featureList, featuresToDisplay.featureType, mapToSearchFilterCategories, mappedSearchFilterInclusions.inclusions])
  const inclusionOverflowLabel = shouldShowCompactMappedInclusionsOverflow(
    mapToSearchFilterCategories && featuresToDisplay.featureType === 'inclusions',
    featuresToDisplay.featureList.length,
    mappedSearchFilterInclusions.inclusions.length,
  ) ? mappedSearchFilterInclusions.showMoreLabel : undefined

  const offerUrl = useOfferUrl(offer, {
    filters,
    offerLinkIncludesFilters: true,
    bundledOfferId: offerMetaData?.bundledOfferId,
  })

  const showModal = useContext(ModalContext)
  const openAllInclusionsModal = useCallback(() => {
    showModal(<Modal
      title={offer.property.name}
      primaryActionText={intl.formatMessage({
        id: '5n7AcIxL6sr_rLtKdVWM',
        defaultMessage: 'View offer',
        description: 'CTA button in experimental hotel inclusions modal to view the offer',
      })}
      primaryActionTo={offerUrl}
    >
      <Group direction="vertical" gap={12}>
        <Group direction="vertical" gap={4}>
          <OfferInclusionsList inclusions={featuresToDisplay.featureList} />
          {!hideLoyaltyInclusions && <LoyaltyInclusions offer={offer} variant="offer-page" />}
        </Group>
        {!mapToSearchFilterCategories && !showPassportInline && <ExperiencesPassportOfferSummary offer={offer} filters={filters} variant="search-tile" />}
      </Group>
    </Modal>)
  }, [offer, featuresToDisplay, offerUrl, showModal, intl, showPassportInline, filters, hideLoyaltyInclusions, mapToSearchFilterCategories])

  const upgradableInclusions = useMemo<Array<App.OfferInclusion>>(() => {
    const inclusionsToDisplay = featuresToDisplay.featureType === 'inclusions' ? featuresToDisplay.featureList : []
    const upgradableInclusions = getUpgradableInclusions(offer.packages, inclusionsToDisplay)
    return upgradableInclusions.length ? upgradableInclusions : EmptyArray
  }, [offer.packages, featuresToDisplay])

  const saveAmount = !!metaData?.pricing?.lowestMemberPrice && !!metaData?.pricing?.lowestPrice ?
    metaData?.pricing?.lowestPrice - metaData?.pricing?.lowestMemberPrice : undefined
  const isFetchingSearchTileFeatures = (isBedbankRate && isFetchingBedbankRates) || isFetchingPropertyAmenities
  const showMaldivesBonusInclusionsCampaignLabel = !mapToSearchFilterCategories && hasMaldivesBonusInclusionsCampaignTag(offer.campaignTags)
  const showDatesAvailable2027CampaignLabel = !mapToSearchFilterCategories && hasDatesAvailable2027CampaignTag(offer.campaignTags)

  if (mapLayout) return <SearchMapInclusions inclusions={featuresToDisplay.featureList} isCard={mapLayout === 'card'} onShowMore={openAllInclusionsModal} />

  return (
    <Group className={ExperimentalHotelSearchTileFeatures.name} direction="vertical" verticalAlign="space-between" grow gap={8} noDisplayWhenEmpty>
      <Group className={ExperimentalHotelSearchTileFeatures.name} direction="vertical" gap={8} noDisplayWhenEmpty>
        {isFetchingSearchTileFeatures && (
          <VerticalSpacer gap={4}>
            <TextLoadingBox typography="body-small" width="60%" />
            <TextLoadingBox typography="body-small" width="80%" />
            <TextLoadingBox typography="body-small" width="40%" />
          </VerticalSpacer>
        )}
        {!!luxPlusInclusionsThatCanBeDisplayed?.length && !hideSecondaryLuxPlusPromos && <LuxPlusInclusions
          luxPlusInclusions={luxPlusInclusionsThatCanBeDisplayed}
          accommodationOffer={offer}
          hideUpsellModal={!!offer.luxPlusTileInclusions?.length}
          showMinStayDescription
          onShowMoreClick={openAllInclusionsModal}
        />}
        {!isFetchingSearchTileFeatures && (
          <>
            <Group direction="vertical" gap={4}>
              <ExperimentalSearchTileFeaturesList
                features={displayFeatures}
                featuresType={featuresToDisplay.featureType}
                leading={featuresToDisplay.featureType === 'amenities' && cancellationInclusionKind ? (
                  <SearchTileFreeCancellationInclusion kind={cancellationInclusionKind} />
                ) : undefined}
                additionalHiddenCount={mapToSearchFilterCategories ? 0 : hiddenFeaturesCount}
                maxInclusionsToShow={getCompactInclusionListLimit(
                  maxFeaturesToShow ?? MAX_INCLUSIONS_TO_SHOW,
                  mapToSearchFilterCategories && featuresToDisplay.featureType === 'inclusions' && !!cancellationInclusion,
                )}
                overflowLabel={inclusionOverflowLabel}
                inclusionSize={mapToSearchFilterCategories ? 'small' : undefined}
                onMoreClick={openAllInclusionsModal}
              />
              {!mapToSearchFilterCategories && showLoyaltyInline && <LoyaltyInclusions offer={offer} variant="search-tile" />}
            </Group>

            {showMaldivesBonusInclusionsCampaignLabel && <MaldivesBonusInclusionsCampaignLabel />}
            {showDatesAvailable2027CampaignLabel && <DatesAvailable2027CampaignLabel />}
            {!hideLuxPlusLabels && !hideSecondaryLuxPlusPromos && !!luxPlusInclusions?.length && !isLuxPlusMember && (
              <LuxPlusLabelWithModal
                type="member-inclusions"
                inclusions={luxPlusInclusions}
                offer={offer}
                hideUpsellModal={!!offer.luxPlusTileInclusions?.length}
              />
            )}
            {!hideMemberSavingsLabel && !hideLuxPlusLabels && !hideSecondaryLuxPlusPromos && !!saveAmount && saveAmount > 0 && !luxPlusInclusions?.length && (
              <LuxPlusLabelWithModal
                type="member-price-offer"
                offer={offer}
                saveAmount={saveAmount}
                joinerExtraSaving={metaData?.pricing?.joinerExtraSaving}
              />
            )}
            {!hideUpgradableInclusionsLabel && !hideSecondaryLuxPlusPromos && !showDatesAvailable2027CampaignLabel && !!upgradableInclusions?.length && <ExperimentalHotelUpgradableInclusionsLabel offer={offer} inclusions={upgradableInclusions} filters={filters} />}
            {!mapToSearchFilterCategories && showPassportInline && <ExperiencesPassportOfferSummary offer={offer} filters={filters} variant="search-tile" />}
          </>
        )}
      </Group>
      {!hideRoomBreakdown && <SearchTileRoomBreakdown
        offer={offer}
        filters={filters}
        isCroCompact={mapToSearchFilterCategories}
      />}
    </Group>
  )
}

export default ExperimentalHotelSearchTileFeatures
